import { z } from "zod";

export const projectPropertyTypes = ["commercial", "office", "condo", "townhouse"] as const;
export const commercialUses = ["retail", "restaurant", "cafe", "clinic", "salon", "hospitality", "other"] as const;
export const propertyRequirements = [
  "building_approval", "fire_safety", "emergency_exit", "electrical_load", "hvac",
  "plumbing", "grease_trap", "exhaust", "accessibility", "signage", "acoustic"
] as const;

export type ProjectPropertyType = typeof projectPropertyTypes[number];
export type CommercialUse = typeof commercialUses[number];
export type PropertyRequirement = typeof propertyRequirements[number];

export const propertyProgramSchema = z.object({
  propertyType: z.enum(projectPropertyTypes),
  commercialUse: z.enum(commercialUses).nullable(),
  requirements: z.array(z.enum(propertyRequirements)).max(propertyRequirements.length)
}).strict().superRefine((value, context) => {
  if (new Set(value.requirements).size !== value.requirements.length) {
    context.addIssue({ code: "custom", path: ["requirements"], message: "Requirements must be unique." });
  }
  if ((value.propertyType === "condo" || value.propertyType === "townhouse") && value.commercialUse !== null) {
    context.addIssue({ code: "custom", path: ["commercialUse"], message: "Residential projects cannot have a commercial use." });
  }
});

const baseCommercialRequirements: readonly PropertyRequirement[] = [
  "building_approval", "fire_safety", "emergency_exit", "electrical_load", "hvac", "accessibility"
];

export function recommendedRequirements(propertyType: ProjectPropertyType, commercialUse: CommercialUse | null): readonly PropertyRequirement[] {
  if (propertyType === "office") return [...baseCommercialRequirements, "acoustic"];
  if (propertyType !== "commercial") return ["building_approval"];
  if (commercialUse === "restaurant" || commercialUse === "cafe") return [...baseCommercialRequirements, "plumbing", "grease_trap", "exhaust", "signage"];
  if (commercialUse === "clinic" || commercialUse === "salon") return [...baseCommercialRequirements, "plumbing", "exhaust", "signage"];
  return [...baseCommercialRequirements, "signage"];
}

export const propertyPriority: Readonly<Record<ProjectPropertyType, 1 | 2 | 3 | 4>> = {
  commercial: 1, office: 2, condo: 3, townhouse: 4
};
